import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiCard, uiFinding, uiGet, uiGhostReadout, uiHeader, uiKpis, uiMoney, uiOpenAction, uiPct, uiPill, uiShareBar, uiTone } from "../ui/kit.js";
import { toast } from "../ui/modals.js";
import { moneyFlowHtml } from "./moneyflow.js";
import { supplyDemandHtml } from "./supplydemand.js";
import { renderDeliveryCapacityPanel, renderFabCapacityPanel, renderRoomCapacityPanel } from "./core/operations.js";

/**
 * Operations, simplified (release 1.8: Rachit found it too dense).
 *
 * Three things on the page: what you can do (the operating model and the
 * company-wide levers, each with a 12-month forecast before you commit),
 * your costs against a normal company (with a one-line verdict), and the
 * supply chain folded to a summary. Everything else (the money flow,
 * supply & demand, the team, the model's detail, the industry's own cycle
 * and capacity) sits in one "More detail" fold. The tab's chart above
 * already shows revenue, margin and people, so there's no KPI row.
 * Data: /api/operations/desk, /api/forecast, /api/actions/execute.
 */

function opsCostBars(lines, revenue) {
  const used = lines.reduce((s, l) => s + (l.pct || 0), 0);
  return `
    <div class="ops-costs">
      ${lines.map((l) => {
        const over = l.pct !== null && l.pct > l.benchmark_pct + 1;
        return `
        <div class="ops-cost">
          <div class="ops-cost-top"><span>${esc(l.label)}</span>
            <span class="ops-cost-num ${over ? "neg" : ""}">${l.pct === null ? "—" : uiPct(l.pct, 1)} <em>· normal ${uiPct(l.benchmark_pct, 0)}</em> · ${uiMoney(l.amount, { compact: true })}/mo</span></div>
          <div class="ops-bar"><div class="ops-bar-fill ${over ? "hot" : ""}" style="width:${Math.min(100, l.pct || 0)}%"></div>
            <div class="ops-bar-mark" style="left:${Math.min(100, l.benchmark_pct)}%" title="Industry norm"></div></div>
          <div class="ops-cost-why">${esc(l.why)}</div>
        </div>`;
      }).join("")}
      <div class="ops-cost ops-keep"><div class="ops-cost-top"><span>What's left before depreciation, interest and tax</span>
        <span class="ops-cost-num ${100 - used < 0 ? "neg" : "pos"}">${uiPct(100 - used, 1)}</span></div></div>
    </div>`;
}

function opsModelCard(desk) {
  return `
    <div class="ops-model">
      ${desk.operating_model.map((m) => `
        <div class="ops-dim">
          <div class="ops-dim-name">${esc(m.category)}${m.defining ? ` ${uiPill("Defining", "info")}` : ""}</div>
          <div class="ops-dim-current">${esc(m.current)}</div>
          ${m.configured
            ? `<div class="ops-tier-dots" title="${esc(m.options[m.current_index].tier)}">${m.options.map((_, i) => `<i class="${i <= m.current_index ? "on" : ""}"></i>`).join("")}</div>`
            : `<div class="bk-sub">Not chosen yet — running at your industry's typical level</div>`}
        </div>`).join("")}
    </div>
    <p class="bk-sub" style="margin-top:10px;">Current model effect: cost of goods ${uiPct(desk.model_effects.cogs_pts, 1, true)} points, growth ${uiPct(desk.model_effects.growth_pts, 2, true)} points a month. Leaner models are cheaper; flagship ones cost more and grow faster.</p>`;
}

function opsModelDrawer(desk) {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal sx-drawer" role="dialog" aria-label="Redesign the operating model">
      <div><div class="sx-eyebrow">Operating model</div><h3>Redesign how the company runs</h3>
        <p class="sx-drawer-desc">Each dimension runs from lean and cheap to flagship and expensive. Changing the model costs a one-off re-tooling fee proportional to how far you move.</p></div>
      <div class="ops-model-form">${desk.operating_model.map((m, i) => `
        <label class="sx-field-label">${esc(m.category)}</label>
        <select data-dim="${i}">${m.configured ? "" : `<option value="" selected>Industry average (not chosen yet)</option>`}${m.options.map((o, oi) => `<option value="${esc(o.key)}" ${oi === m.current_index ? "selected" : ""}>${esc(o.label)} — ${esc(o.tier)}</option>`).join("")}</select>`).join("")}
      </div>
      <div class="sx-forecast" id="ops-model-fc"></div>
      <div class="modal-actions"><button type="button" id="ops-model-cancel">Cancel</button><button type="button" class="primary" id="ops-model-go">Adopt this model</button></div>
    </div>`;
  document.body.appendChild(backdrop);
  // an unset dimension counts as its Standard option once the model is adopted
  const selections = () => Object.fromEntries(desk.operating_model.map((m, i) => [m.category, backdrop.querySelector(`[data-dim="${i}"]`).value || "1"]));
  const initial = JSON.stringify(desk.operating_model.map((_, i) => backdrop.querySelector(`[data-dim="${i}"]`).value));
  const changed = () => JSON.stringify(desk.operating_model.map((_, i) => backdrop.querySelector(`[data-dim="${i}"]`).value)) !== initial;
  const lane = `ops-model-${Date.now().toString(36)}`;
  let seq = 0, timer = null, ctrl = null;
  const stop = () => { clearTimeout(timer); seq++; if (ctrl) ctrl.abort(); ctrl = null; };
  const refresh = () => {
    stop();
    const host = backdrop.querySelector("#ops-model-fc");
    backdrop.querySelector("#ops-model-go").disabled = !changed();
    if (!changed()) { host.innerHTML = `<div class="sx-forecast-loading">Change any dimension to see what it would do over the next 12 months.</div>`; return; }
    host.innerHTML = `<div class="sx-forecast-loading">Running the next 12 months with this model…</div>`;
    // wait until the player stops changing dropdowns, so forecasts don't pile up on the server
    timer = setTimeout(async () => {
      const mine = ++seq;
      ctrl = typeof AbortController === "function" ? new AbortController() : null;
      try {
        const r = await postJSON("/forecast", { action: { type: "configure_operations", params: { selections: selections() } }, months: 12, seeds: 3, lane },
                                 { signal: ctrl ? ctrl.signal : undefined });
        if (mine === seq) host.innerHTML = `<div class="sx-fc-note" style="margin-bottom:8px;">${esc(r.action_message || "")}</div>${uiGhostReadout(r)}`;
      } catch (e) { if (mine === seq && e.name !== "AbortError" && e.message !== "superseded") host.innerHTML = `<div class="sx-forecast-error">${esc(e.message)}</div>`; }
    }, 700);
  };
  backdrop.querySelectorAll("select").forEach((s) => s.addEventListener("change", refresh));
  const close = () => { stop(); backdrop.remove(); };
  backdrop.querySelector("#ops-model-cancel").addEventListener("click", close);
  backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
  backdrop.querySelector("#ops-model-go").addEventListener("click", async () => {
    const go = backdrop.querySelector("#ops-model-go");
    stop(); // drop any forecast still on its way: the decision is made
    go.disabled = true;
    go.textContent = "Adopting…";
    try {
      const r = await postJSON("/actions/execute", { action: { type: "configure_operations", params: { selections: selections() } } });
      close();
      toast(r.message, /can't|cannot|not enough|insufficient/i.test(r.message || ""));
      await window.refreshAll();
    } catch (e) {
      go.disabled = false;
      go.textContent = "Adopt this model";
      toast(e.message || "The model couldn't be adopted.", true);
    }
  });
  refresh();
}

function opsTeamCard(desk) {
  const tones = ["t1", "t2", "t3", "t4", "t5"];
  return `
    ${uiShareBar(desk.workforce.map((d, i) => ({ label: d.department, value: d.headcount, tone: tones[i % tones.length] })))}
    <table class="sx-table" style="margin-top:12px;">
      <tr><th>Department</th><th class="num">People</th><th class="num">Avg salary</th><th class="num">Cost / yr</th></tr>
      ${desk.workforce.map((d) => `<tr><td>${esc(d.department)}</td><td class="num">${d.headcount}</td><td class="num">${uiMoney(d.avg_annual_salary, { compact: true })}</td><td class="num">${uiMoney(d.annual_cost, { compact: true })}</td></tr>`).join("")}
    </table>
    <p class="bk-sub" style="margin-top:8px;">Headcount follows your spending: hiring and attrition happen automatically as budgets change. Executives are on the Team &amp; Talent tab.</p>`;
}

function opsSupplyCard(sc) {
  const strategyLabel = { single: "Single source", dual: "Dual source", multi: "Multi-source", partnership: "Strategic partnership", vertical: "Made in-house" };
  return `
    <div class="ops-supply-top">
      ${uiKpis([
        { label: "Inventory on hand", value: `${sc.inventory_days.toFixed(0)} days`, sub: uiMoney(sc.inventory_value, { compact: true }) },
        { label: "Safety stock", value: `${sc.safety_stock_days} days`, sub: sc.carrying_cost_per_month ? `${uiMoney(sc.carrying_cost_per_month)}/mo to carry` : "No buffer" },
        { label: "Output lost to shortages", value: uiPct(sc.output_loss_pct, 1), tone: sc.output_loss_pct > 0 ? "neg" : "" },
        { label: "Sourcing effect on costs", value: uiPct(sc.cogs_price_effect_pct, 1, true), tone: uiTone(-sc.cogs_price_effect_pct) },
      ])}
    </div>
    ${(sc.alerts || []).length ? `<div class="sx-findings" style="margin:12px 0;">${sc.alerts.map((a) => uiFinding({ severity: 2, title: a.title || "Supply alert", detail: a.detail || a.message || String(a) })).join("")}</div>` : ""}
    <div class="ops-inputs">${sc.inputs.map((inp) => `
      <div class="ops-input">
        <div class="ops-input-head">
          <div><div class="ops-dim-name">${esc(inp.label)}</div>
            <div class="bk-sub">${uiPct(inp.share_of_cogs * 100, 0)} of cost of goods · criticality ${Math.round(inp.criticality * 100)}/100 · price volatility ${Math.round(inp.volatility * 100)}/100</div></div>
          ${uiPill(strategyLabel[inp.strategy] || inp.strategy_label, inp.strategy === "single" ? "warn" : inp.strategy === "vertical" ? "good" : "info")}
        </div>
        ${inp.shortage_pct > 0 || inp.price_spike_pct > 0 ? `<div class="bk-sub neg">Shortage ${uiPct(inp.shortage_pct, 1)} · price spike ${uiPct(inp.price_spike_pct, 1)}</div>` : ""}
        <table class="sx-table ops-suppliers">
          ${inp.suppliers.filter((s) => s.share_pct > 0 || s.selected).map((s) => `<tr><td>${esc(s.name)}<div class="bk-sub">${esc(s.country)} · ${esc(s.region)}</div></td>
            <td class="num">${uiPct(s.share_pct * 100, 0)}</td><td class="num">${uiPct(s.reliability_pct, 0)} reliable</td><td class="num">${s.lead_time_days}d lead</td></tr>`).join("")}
        </table>
        <div class="ops-input-actions">
          ${inp.locked ? `<span class="bk-sub">Locked into a partnership until M${inp.contract_until_month}.</span>` : inp.strategy === "vertical" ? "" :
            ["single", "dual", "multi", "partnership"].filter((k) => k !== inp.strategy).map((k) =>
              `<button type="button" data-sourcing="${esc(inp.key)}" data-strategy="${k}">${esc(strategyLabel[k])}</button>`).join("")}
          ${inp.vertical_status === "none" && !inp.locked ? `<button type="button" data-vertical="${esc(inp.key)}">Make it in-house</button>` :
            inp.vertical_status === "building" ? `<span class="bk-sub">In-house production ready M${inp.vertical_ready_month}.</span>` : ""}
        </div>
      </div>`).join("")}</div>
    <div class="ops-input-actions" style="margin-top:12px;">
      <span class="bk-sub">Safety stock:</span>
      ${sc.safety_stock_options.map((d) => `<button type="button" class="${d === sc.safety_stock_days ? "primary" : ""}" data-safety="${d}">${d} days</button>`).join("")}
    </div>`;
}

window.renderOperations = async function (main, snap) {
  const desk = await uiGet("/operations/desk");
  const dyn = desk.industry_dynamics || {};
  const dynMetrics = Object.entries(dyn).filter(([k, v]) => !["mechanic", "note"].includes(k) && typeof v === "number");
  const sc = desk.supply_chain;
  const supplySummary = sc ? `${sc.inputs.length} input${sc.inputs.length === 1 ? "" : "s"} · ${sc.inventory_days.toFixed(0)} days of stock · `
    + `${sc.output_loss_pct > 0 ? `${uiPct(sc.output_loss_pct, 1)} of output lost to shortages` : "no shortages"}` : "";
  const over = desk.cost_lines.filter((l) => l.pct !== null && l.pct > l.benchmark_pct + 1);
  main.innerHTML = `
    <div class="sx ops-simple">
      ${uiHeader(`Operations — Month ${snap.month}`, "How the company runs, made simple: what you can change, where your costs are high, and your suppliers. Every change shows a 12-month forecast before you commit.")}
      <section class="sx-playgroup">
        <div class="sx-playgroup-head"><h3 class="sx-playgroup-title">What you can do</h3><span class="sx-playgroup-sub">Pick one: you'll see what it does over the next 12 months before you commit</span></div>
        <div class="sx-plays">
          <button type="button" class="sx-play" id="ops-redesign">
            <span class="sx-play-label">Redesign how you operate</span><span class="sx-play-desc">Five choices, from lean and cheap to flagship and fast-growing: production, sourcing, quality, inventory and workforce.</span>
            <span class="sx-play-foot"><span>Operating model</span><strong>One-off fee</strong></span>
          </button>
          ${desk.levers.map((l) => `
          <button type="button" class="sx-play" data-lever="${esc(l.id)}">
            <span class="sx-play-label">${esc(l.label)}</span><span class="sx-play-desc">${esc(l.description)}</span>
            <span class="sx-play-foot"><span>${esc(l.group)}</span><strong>${l.input.kind === "amount" ? "You choose" : "Free"}</strong></span>
          </button>`).join("")}</div>
      </section>
      ${uiCard("Your costs against a normal company", `
        <p class="ops-verdict ${over.length ? "warn" : "ok"}">${over.length
          ? `Above normal: <b>${over.map((l) => esc(l.label.toLowerCase())).join(", ")}</b>. That's where to look first.`
          : "Every cost is at or below what's normal for your industry."}</p>
        ${opsCostBars(desk.cost_lines, desk.revenue)}`,
        { sub: "Share of revenue; the white marker is normal for your industry" })}
      ${sc ? `<details class="sx-card ops-fold"><summary><span class="ops-fold-title">Supply chain</span><span class="ops-fold-sub">${esc(supplySummary)}</span></summary>
        ${opsSupplyCard(sc)}</details>` : ""}
      <details class="sx-card ops-fold" id="ops-more"><summary><span class="ops-fold-title">More detail</span>
        <span class="ops-fold-sub">Where the money goes · supply &amp; demand · your team · operating model${dyn.mechanic ? ` · ${esc(dyn.mechanic)}` : ""}</span></summary>
        <div class="ops-more-body">
          ${uiCard("Where the money goes", moneyFlowHtml(desk.money_flow || {}), { sub: "Last month: revenue on the left, where each dollar ended up on the right" })}
          <div id="ops-sd"></div>
          <div class="sx-grid-2">
            ${uiCard("Operating model", opsModelCard(desk), { sub: "The five choices that define how you operate" })}
            ${uiCard("Team", opsTeamCard(desk), { sub: `${desk.headcount} people by department` })}
          </div>
          ${dyn.mechanic ? uiCard(dyn.mechanic, `
            ${dynMetrics.length ? uiKpis(dynMetrics.slice(0, 4).map(([k, v]) => ({ label: k.replace(/_/g, " "), value: String(v) }))) : ""}
            <p class="bk-sub" style="margin-top:10px;">${esc(dyn.note || "")}</p>`, { sub: "Your industry's own force of nature" }) : ""}
          <div id="ops-config-summary" style="display:none;"></div>
        </div>
      </details>
    </div>`;
  // supply & demand loads only when "More detail" is opened: the page shows at once
  main.querySelector("#ops-more").addEventListener("toggle", (e) => {
    const host = main.querySelector("#ops-sd");
    if (!e.target.open || !host || host.dataset.loaded) return;
    host.dataset.loaded = "1";
    host.innerHTML = uiCard("Supply & demand", `<div class="sx-forecast-loading">Reading the market…</div>`);
    uiGet("/supply-demand").then((sd) => { host.innerHTML = uiCard("Supply & demand", supplyDemandHtml(sd), { sub: "From your suppliers to the whole market, and what limits you" }); })
      .catch(() => host.remove());
  });

  main.querySelector("#ops-redesign").addEventListener("click", () => opsModelDrawer(desk));
  main.querySelectorAll("[data-lever]").forEach((b) => b.addEventListener("click", () => {
    const l = desk.levers.find((x) => x.id === b.dataset.lever);
    uiOpenAction({ eyebrow: "Company-wide lever", title: l.label, description: l.description, effect: l.effect, risk: l.risk,
                   input: l.input, action: l.action, costFor: (v) => (l.input.kind === "amount" ? v : 0) });
  }));
  const supplyAction = (title, description, action) => uiOpenAction({ eyebrow: "Supply chain", title, description,
    effect: "Changes cost, reliability and exposure from next month.", risk: "Switching suppliers costs a one-off fee; partnerships lock you in for 12 months.",
    input: null, action, costFor: () => null });
  main.querySelectorAll("[data-sourcing]").forEach((b) => b.addEventListener("click", () =>
    supplyAction(`Source ${b.dataset.sourcing.replace(/_/g, " ")}: ${b.textContent}`, "Reallocate this input across suppliers.",
                 { type: "set_sourcing_strategy", params: { input_key: b.dataset.sourcing, strategy: b.dataset.strategy } })));
  main.querySelectorAll("[data-vertical]").forEach((b) => b.addEventListener("click", () =>
    supplyAction("Make it in-house", "Build your own production for this input: a big investment and months of build time, then no supplier risk.",
                 { type: "start_vertical_integration", params: { input_key: b.dataset.vertical } })));
  main.querySelectorAll("[data-safety]").forEach((b) => b.addEventListener("click", () =>
    supplyAction(`Hold ${b.dataset.safety} days of safety stock`, "A buffer of extra inventory against shortages. It ties up cash and costs money to carry.",
                 { type: "set_safety_stock", params: { days: parseInt(b.dataset.safety, 10) } })));

  // industry capacity models (fab / rooms / billable delivery) attach after #ops-config-summary
  const [delivery, rooms, fab] = await Promise.all([
    uiGet("/delivery-capacity").catch(() => null), uiGet("/room-capacity").catch(() => null), uiGet("/fab-capacity").catch(() => null)]);
  if (delivery) renderDeliveryCapacityPanel(delivery, window.__company);
  if (rooms) renderRoomCapacityPanel(rooms);
  if (fab && typeof fab.utilization_pct === "number") renderFabCapacityPanel(fab);
};
